import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import PeriodFilter, { calcRange, toIso } from '../components/PeriodFilter';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const THX_RED = '#B71530';

function MetricCard({ label, value, icon, color }) {
  return (
    <div className="col-6 col-md mb-0">
      <div className="card h-100" style={{ borderLeft: `4px solid ${color || THX_RED}`, borderRadius: '8px' }}>
        <div className="card-body py-3">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
              </div>
              <div style={{ fontSize: '26px', fontWeight: '700', color: '#212529', marginTop: '2px' }}>
                {value ?? '–'}
              </div>
            </div>
            {icon && (
              <i className={`bi ${icon}`} style={{ fontSize: '28px', color: color || THX_RED, opacity: 0.5 }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const isAppAdmin = user?.role === 'app_admin';

  const [orgs, setOrgs]             = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [stats, setStats]           = useState(null);
  const [status, setStatus]         = useState(null);
  const [newSince, setNewSince]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [actionMsg, setActionMsg]   = useState('');
  const [cancelling, setCancelling] = useState(false);
  const cancelPollRef = useRef(null);

  // Period filter state — default 30 days
  const [preset, setPreset]     = useState('30');
  const [dateFrom, setDateFrom] = useState(() => calcRange('30').from);
  const [dateTo,   setDateTo]   = useState(() => calcRange('30').to);

  // App-admin: load org list
  useEffect(() => {
    if (!isAppAdmin) return;
    client.get('/orgs/')
      .then(r => {
        const list = r.data.organizations || [];
        setOrgs(list);
        if (list.length > 0) setSelectedOrgId(list[0].id);
      })
      .catch(() => {});
  }, [isAppAdmin]);

  const fetchAll = useCallback(() => {
    const params = new URLSearchParams();
    if (isAppAdmin && selectedOrgId) params.set('org_id', selectedOrgId);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo)   params.set('date_to',   dateTo);
    const qs = params.toString() ? `?${params.toString()}` : '';

    Promise.all([
      client.get(`/dashboard/stats${qs}`),
      client.get('/dashboard/status'),
      client.get('/dashboard/new-since-last-visit'),
    ])
      .then(([s, st, ns]) => {
        setStats(s.data);
        setStatus(st.data);
        setNewSince(ns.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAppAdmin, selectedOrgId, dateFrom, dateTo]);

  useEffect(() => {
    if (isAppAdmin && selectedOrgId === null) return;
    fetchAll();
    const interval = setInterval(() => {
      client.get('/dashboard/status').then(r => setStatus(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchAll, selectedOrgId]);

  const handlePreset = (days) => {
    const range = calcRange(days);
    setPreset(days);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  const handleFromChange = (e) => {
    setDateFrom(e.target.value);
    setPreset('custom');
  };

  const handleToChange = (e) => {
    setDateTo(e.target.value);
    setPreset('custom');
  };

  const handleStart = async () => {
    try {
      await client.post('/dashboard/start');
      setActionMsg('Processing started');
      setTimeout(() => setActionMsg(''), 3000);
      fetchAll();
    } catch (e) {
      setActionMsg(e?.response?.data?.detail || 'Could not start processing');
      setTimeout(() => setActionMsg(''), 4000);
    }
  };

  const handleStop = async () => {
    try {
      await client.post('/dashboard/stop');
      setCancelling(true);
      setActionMsg('');
      if (cancelPollRef.current) clearInterval(cancelPollRef.current);
      cancelPollRef.current = setInterval(async () => {
        try {
          const r = await client.get('/dashboard/status');
          setStatus(r.data);
          if (!r.data.running) {
            clearInterval(cancelPollRef.current);
            cancelPollRef.current = null;
            setCancelling(false);
            fetchAll();
          }
        } catch { /* ignore */ }
      }, 1000);
    } catch (e) {
      setActionMsg(e?.response?.data?.detail || 'Could not stop processing');
      setTimeout(() => setActionMsg(''), 4000);
    }
  };

  useEffect(() => () => { if (cancelPollRef.current) clearInterval(cancelPollRef.current); }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <div className="spinner-border text-danger" />
      </div>
    );
  }

  const s = stats || {};
  const pct = s.total_emails > 0 ? Math.round((s.processed_emails / s.total_emails) * 100) : 0;

  const mailboxLabels    = (s.by_mailbox || []).map(m => m.mailbox || 'Unknown');
  const mailboxTotals    = (s.by_mailbox || []).map(m => m.total);
  const mailboxProcessed = (s.by_mailbox || []).map(m => m.processed);

  const docTypeLabels = (s.by_doc_type || []).map(d => d.doc_type);
  const docTypeCounts = (s.by_doc_type || []).map(d => d.count);

  const selectedOrg = orgs.find(o => o.id === selectedOrgId);

  return (
    <div>
      {/* App-admin org selector */}
      {isAppAdmin && orgs.length > 0 && (
        <div className="card mb-3" style={{ borderLeft: `4px solid ${THX_RED}` }}>
          <div className="card-body py-2 d-flex align-items-center" style={{ gap: '12px' }}>
            <i className="bi bi-diagram-3" style={{ fontSize: '16px', color: THX_RED }} />
            <span style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>Viewing org:</span>
            <select
              className="form-control form-control-sm"
              style={{ maxWidth: '260px', fontWeight: 500 }}
              value={selectedOrgId || ''}
              onChange={e => setSelectedOrgId(Number(e.target.value))}
            >
              {orgs.map(o => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.subdomain}) — {o.user_count} user{o.user_count !== 1 ? 's' : ''}
                </option>
              ))}
            </select>
            {selectedOrg && (
              <span style={{ fontSize: '12px', color: '#6c757d' }}>
                <code>{selectedOrg.subdomain}.yourdomain.com</code>
              </span>
            )}
          </div>
        </div>
      )}

      {/* New since last visit banner */}
      {newSince && (newSince.new_invoices > 0 || newSince.new_exceptions > 0) && (
        <div className="alert alert-info d-flex align-items-center mb-3" style={{ fontSize: '14px' }}>
          <i className="bi bi-info-circle me-2" />
          Since your last visit: <strong className="mx-1">{newSince.new_invoices}</strong> new invoices,{' '}
          <strong className="mx-1">{newSince.new_exceptions}</strong> new exceptions.
        </div>
      )}

      {/* Status banner */}
      {status && !isAppAdmin && (
        <div
          className={`alert mb-3 d-flex align-items-center justify-content-between ${
            status.running ? 'alert-warning' : status.error ? 'alert-danger' : 'alert-success'
          }`}
          style={{ fontSize: '14px' }}
        >
          <div>
            {cancelling ? (
              <span className="text-warning">
                <i className="bi bi-hourglass-split me-2" />
                Stopping… finishing current step, please wait
              </span>
            ) : status.running ? (
              <span>
                <i className="bi bi-hourglass-split me-2" />
                {`Processing: ${status.stage || 'Running...'}`}
              </span>
            ) : status.error ? (
              <span className="text-danger">
                <i className="bi bi-exclamation-circle me-2" />
                {`Error: ${status.error}`}
              </span>
            ) : (
              <span className="text-success">
                <i className="bi bi-check-circle me-2" />
                Pipeline idle
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm"
              style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
              onClick={handleStart}
              disabled={status.running}
            >
              <i className="bi bi-play-fill me-1" /> Start Processing
            </button>
            {cancelling ? (
              <button className="btn btn-sm btn-secondary" disabled>
                <span className="spinner-border spinner-border-sm me-1" />
                Cancelling…
              </button>
            ) : (
              <button className="btn btn-sm btn-outline-secondary" onClick={handleStop} disabled={!status.running}>
                <i className="bi bi-stop-fill me-1" /> Stop
              </button>
            )}
          </div>
        </div>
      )}

      {actionMsg && (
        <div className="alert alert-info py-2 mb-3" style={{ fontSize: '14px' }}>{actionMsg}</div>
      )}

      {/* Metric cards – row 1 */}
      <div className="row mb-2">
        <MetricCard label="Total Emails"       value={s.total_emails}       icon="bi-envelope"            color="#6c757d" />
        <MetricCard label="Processed"          value={s.processed_emails}   icon="bi-envelope-check"      color="#198754" />
        <MetricCard label="Invoices Extracted" value={s.total_invoices}     icon="bi-receipt"             color={THX_RED} />
        <MetricCard label="Open Exceptions"    value={s.open_exceptions}    icon="bi-exclamation-triangle" color="#dc3545" />
        <MetricCard label="Needs Review"       value={s.needs_review}       icon="bi-eye"                 color="#fd7e14" />
      </div>

      {/* Metric cards – row 2 */}
      <div className="row row-cols-2 row-cols-md-5 g-2 mb-0">
        <MetricCard label="Documents"   value={s.total_documents}   icon="bi-files"       color="#0d6efd" />
        <MetricCard label="Credit Notes" value={s.total_credit_notes} icon="bi-credit-card" color="#6f42c1" />
        <MetricCard label="Suppliers"   value={s.total_suppliers}   icon="bi-building"    color="#20c997" />
        <MetricCard label="Resolved"    value={s.resolved_exceptions} icon="bi-check-circle" color="#198754" />
        <MetricCard label="% Processed" value={`${pct}%`}           icon="bi-percent"     color="#0dcaf0" />
      </div>

      {/* Period filter */}
      <PeriodFilter
        preset={preset} dateFrom={dateFrom} dateTo={dateTo}
        onPresetChange={d => { const r = calcRange(d); setPreset(d); setDateFrom(r.from); setDateTo(r.to); }}
        onFromChange={e => { setDateFrom(e.target.value); setPreset('custom'); }}
        onToChange={e => { setDateTo(e.target.value); setPreset('custom'); }}
      />

      {/* Charts */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header fw-semibold" style={{ fontSize: '14px' }}>Emails by Mailbox</div>
            <div className="card-body">
              {mailboxLabels.length > 0 ? (
                <Bar
                  data={{
                    labels: mailboxLabels,
                    datasets: [
                      { label: 'Total',     data: mailboxTotals,    backgroundColor: 'rgba(183,21,48,0.7)' },
                      { label: 'Processed', data: mailboxProcessed, backgroundColor: 'rgba(25,135,84,0.7)' },
                    ],
                  }}
                  options={{ responsive: true, plugins: { legend: { position: 'top' } }, scales: { y: { beginAtZero: true } } }}
                />
              ) : (
                <p className="text-muted text-center my-4" style={{ fontSize: '14px' }}>No data</p>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-header fw-semibold" style={{ fontSize: '14px' }}>Documents by Type</div>
            <div className="card-body">
              {docTypeLabels.length > 0 ? (
                <Bar
                  data={{
                    labels: docTypeLabels,
                    datasets: [{
                      label: 'Count',
                      data: docTypeCounts,
                      backgroundColor: ['#B71530','#198754','#0d6efd','#fd7e14','#6f42c1','#20c997','#ffc107'],
                    }],
                  }}
                  options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }}
                />
              ) : (
                <p className="text-muted text-center my-4" style={{ fontSize: '14px' }}>No data</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Exceptions by type + Last run */}
      <div className="row g-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header fw-semibold" style={{ fontSize: '14px' }}>Exceptions by Type</div>
            <div className="card-body p-0">
              <table className="table table-sm mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th>Type</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {(s.by_exception_type || []).length === 0 && (
                    <tr><td colSpan="3" className="text-center text-muted py-3">No exceptions</td></tr>
                  )}
                  {(s.by_exception_type || []).map(row => (
                    <tr key={row.exception_type}>
                      <td>{row.exception_type?.replace(/_/g, ' ')}</td>
                      <td className="text-end">{row.count}</td>
                      <td className="text-end">
                        <span className={`badge ${row.open_count > 0 ? 'bg-danger' : 'bg-success'}`}>
                          {row.open_count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-header fw-semibold" style={{ fontSize: '14px' }}>Last Processing Run</div>
            <div className="card-body">
              {s.last_run ? (
                <dl className="row mb-0" style={{ fontSize: '13px' }}>
                  <dt className="col-sm-5">Status</dt>
                  <dd className="col-sm-7">
                    <span className={`badge ${
                      s.last_run.status === 'completed' ? 'bg-success' :
                      s.last_run.status === 'failed'    ? 'bg-danger'  :
                      s.last_run.status === 'cancelled' ? 'bg-warning text-dark' :
                      'bg-primary'
                    }`}>
                      {s.last_run.status}
                    </span>
                  </dd>
                  <dt className="col-sm-5">Started</dt>
                  <dd className="col-sm-7">{s.last_run.started_at ? new Date(s.last_run.started_at).toLocaleString() : '–'}</dd>
                  <dt className="col-sm-5">Completed</dt>
                  <dd className="col-sm-7">{s.last_run.completed_at ? new Date(s.last_run.completed_at).toLocaleString() : '–'}</dd>
                  <dt className="col-sm-5">Emails</dt>
                  <dd className="col-sm-7">{s.last_run.emails_processed}</dd>
                  <dt className="col-sm-5">Invoices</dt>
                  <dd className="col-sm-7">{s.last_run.invoices_extracted}</dd>
                  <dt className="col-sm-5">Exceptions</dt>
                  <dd className="col-sm-7">{s.last_run.exceptions_raised}</dd>
                  {s.last_run.error_message && (
                    <>
                      <dt className="col-sm-5 text-danger">Error</dt>
                      <dd className="col-sm-7 text-danger" style={{ fontSize: '12px' }}>{s.last_run.error_message}</dd>
                    </>
                  )}
                </dl>
              ) : (
                <p className="text-muted text-center my-3" style={{ fontSize: '14px' }}>No runs yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
